import {TapPost, TapPostsResponse, TapTag, TapTagsResponse, ExploreResponse} from './types'

/**
 * Client for TAP (Tag-based AT Protocol) API
 * TAP API validates Origin header to restrict access to batesky.app only
 * In development, uses webpack proxy to bypass CORS
 */
export class TapApiClient {
  private baseUrl =
    process.env.NODE_ENV === 'development' &&
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost'
      ? '/tap-api' // Use webpack proxy in local development
      : 'https://tap.batesky.app'

  /**
   * Get WebSocket URL for real-time updates
   */
  getWebSocketUrl(): string {
    const httpUrl = this.baseUrl

    // Handle relative path in development (webpack proxy)
    if (httpUrl.startsWith('/')) {
      if (typeof window !== 'undefined') {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        return `${wsProtocol}//${window.location.host}${httpUrl}/channel`
      }
      // Fallback for non-browser environments
      return `ws://localhost:19006${httpUrl}/channel`
    }

    // Handle absolute URL (production)
    const wsProtocol = httpUrl.startsWith('https') ? 'wss' : 'ws'
    const wsUrl = httpUrl.replace(/^https?/, wsProtocol)
    return `${wsUrl}/channel`
  }

  /**
   * Get posts by tag slug - calls TAP API directly (now has CORS enabled)
   *
   * Note: TAP API returns record format for embeds (blob refs only).
   * For videos, the query layer fetches complete post data from Bluesky API
   * to get the playlist URLs needed for playback.
   */
  async getPostsByTag({
    slug,
    limit = 50,
    offset = 0,
    accessToken,
  }: {
    slug: string
    limit?: number
    offset?: number
    accessToken?: string
  }): Promise<TapPostsResponse> {
    const url = `${this.baseUrl}/api/tags/${slug}/posts?limit=${limit}&offset=${offset}`
    const response = await this.fetch(url, false, accessToken)

    // Transform new backend format to AT Protocol format
    if (response.posts) {
      response.posts = response.posts.map((post: any) => {
        // If already in AT Protocol format, process normally
        if (post.author) {
          return this.normalizePost(post)
        }

        // New backend format needs transformation
        // Backend returns: {did, created_at, has_images, etc.}
        // Frontend needs: PostView with author object

        // Extract embed from record and convert to view format
        let embed = undefined
        if (post.record?.embed) {
          const recordEmbed = post.record.embed
          if (recordEmbed.$type === 'app.bsky.embed.images') {
            // Convert blob refs to CDN URLs
            embed = {
              $type: 'app.bsky.embed.images#view',
              images: recordEmbed.images?.map((img: any) => {
                const cid = img.image?.ref?.$link
                const did = post.did
                // Construct Bluesky CDN URL from blob ref
                const url = cid && did
                  ? `https://cdn.bsky.app/img/feed_fullsize/plain/${did}/${cid}@jpeg`
                  : ''
                return {
                  thumb: url,
                  fullsize: url,
                  alt: img.alt || '',
                  aspectRatio: img.aspectRatio,
                }
              }) || [],
            }
          } else if (recordEmbed.$type === 'app.bsky.embed.video') {
            // Video embeds: Backend should provide the view format with playlist URL
            // If backend returns record format, we can't construct playlist URL client-side
            // Log what we're getting to debug
            console.log('[TapApiClient] Video embed from backend:', {
              hasPlaylist: !!recordEmbed.playlist,
              hasVideo: !!recordEmbed.video,
              structure: recordEmbed,
            })

            // Check if backend already provided view format
            if (recordEmbed.playlist) {
              // Backend provided view format with playlist - use directly
              embed = {
                $type: 'app.bsky.embed.video#view',
                ...recordEmbed,
              }
            } else if (recordEmbed.video?.playlist) {
              // Backend nested playlist in video object
              embed = {
                $type: 'app.bsky.embed.video#view',
                video: recordEmbed.video,
              }
            } else {
              // Backend only provided record format - this won't work for playback
              // We need the backend to provide the playlist URL
              console.error('[TapApiClient] Video embed missing playlist URL:', recordEmbed)
              embed = {
                $type: 'app.bsky.embed.video#view',
                video: recordEmbed.video || recordEmbed,
              }
            }
          } else {
            embed = recordEmbed
          }
        }

        const transformedPost = {
          uri: post.uri,
          cid: post.cid,
          author: {
            did: post.did,
            handle: 'user.bsky.social', // Placeholder handle
            displayName: 'User', // Placeholder display name
            avatar: '',
            viewer: {},
            labels: [],
          },
          record: post.record,
          embed,
          replyCount: post.reply_count || 0,
          repostCount: post.repost_count || 0,
          likeCount: post.like_count || 0,
          quoteCount: post.quote_count || 0,
          indexedAt: post.created_at
            ? new Date(post.created_at * 1000).toISOString()
            : new Date().toISOString(),
          viewer: {},
          labels: [],
          // Keep TAP extensions
          hasImages: post.has_images,
          hasVideo: post.has_video,
          hasGif: post.has_gif,
          imageCount: post.image_count,
          tags: post.tags,
          tagsComplete: post.tags_complete,
        }

        return this.normalizePost(transformedPost)
      })
    }

    // Debug: Log transformed posts
    if (response.posts && response.posts.length > 0) {
      console.log('[TapApiClient] Transformed posts:', {
        count: response.posts.length,
        firstPost: {
          uri: response.posts[0].uri?.slice(-15),
          hasAuthor: !!response.posts[0].author,
          authorDid: response.posts[0].author?.did?.slice(0, 20),
        },
      })
    }

    return response
  }

  /**
   * Normalize post to fix video embed structure and other issues
   */
  private normalizePost(post: any): any {
    // Fix: TAP API returns video embed with incorrect structure
    // It nests playlist inside a "video" object, but AT Protocol expects it at root level
    if (post.embed?.$type === 'app.bsky.embed.video#view' && post.embed.video) {
      const {video, ...restEmbed} = post.embed
      return {
        ...post,
        embed: {
          ...restEmbed,
          cid: video.cid || post.cid,
          playlist: video.playlist,
          thumbnail: video.thumbnail,
          alt: video.alt,
          aspectRatio: video.aspectRatio,
        },
      }
    }
    return post
  }

  /**
   * Get all posts (without tag filter) - calls TAP API directly
   * Used for "All Videos" view
   * Note: This endpoint requires TAP authentication, not Bluesky auth
   */
  async getAllPosts({
    limit = 50,
    offset = 0,
    accessToken,
  }: {
    limit?: number
    offset?: number
    accessToken?: string
  }): Promise<TapPostsResponse> {
    const url = `${this.baseUrl}/api/posts?limit=${limit}&offset=${offset}&contentType=video`
    // Don't pass accessToken - this endpoint requires TAP auth which we don't have for public viewing
    const response = await this.fetch(url, false)

    // Transform posts using same logic as getPostsByTag
    if (response.posts) {
      response.posts = response.posts.map((post: any) => {
        if (post.author) {
          return this.normalizePost(post)
        }

        // Transform backend format
        let embed = undefined
        if (post.record?.embed) {
          const recordEmbed = post.record.embed
          if (recordEmbed.$type === 'app.bsky.embed.images') {
            embed = {
              $type: 'app.bsky.embed.images#view',
              images: recordEmbed.images?.map((img: any) => {
                const cid = img.image?.ref?.$link
                const did = post.did
                const url = cid && did
                  ? `https://cdn.bsky.app/img/feed_fullsize/plain/${did}/${cid}@jpeg`
                  : ''
                return {
                  thumb: url,
                  fullsize: url,
                  alt: img.alt || '',
                  aspectRatio: img.aspectRatio,
                }
              }) || [],
            }
          } else if (recordEmbed.$type === 'app.bsky.embed.video') {
            if (recordEmbed.playlist) {
              embed = {
                $type: 'app.bsky.embed.video#view',
                ...recordEmbed,
              }
            } else if (recordEmbed.video?.playlist) {
              embed = {
                $type: 'app.bsky.embed.video#view',
                video: recordEmbed.video,
              }
            } else {
              embed = {
                $type: 'app.bsky.embed.video#view',
                video: recordEmbed.video || recordEmbed,
              }
            }
          } else {
            embed = recordEmbed
          }
        }

        const transformedPost = {
          uri: post.uri,
          cid: post.cid,
          author: {
            did: post.did,
            handle: 'user.bsky.social',
            displayName: 'User',
            avatar: '',
            viewer: {},
            labels: [],
          },
          record: post.record,
          embed,
          replyCount: post.reply_count || 0,
          repostCount: post.repost_count || 0,
          likeCount: post.like_count || 0,
          quoteCount: post.quote_count || 0,
          indexedAt: post.created_at
            ? new Date(post.created_at * 1000).toISOString()
            : new Date().toISOString(),
          viewer: {},
          labels: [],
          hasImages: post.has_images,
          hasVideo: post.has_video,
          hasGif: post.has_gif,
          imageCount: post.image_count,
          tags: post.tags,
          tagsComplete: post.tags_complete,
        }

        return this.normalizePost(transformedPost)
      })
    }

    return response
  }

  /**
   * Get global video feed - all video posts across tags
   * Sorted by indexedAt DESC server-side
   * Uses new optimized /api/posts/videos endpoint
   */
  async getVideoFeed({
    limit = 50,
    offset = 0,
    accessToken,
  }: {
    limit?: number
    offset?: number
    accessToken?: string
  }): Promise<Omit<TapPostsResponse, 'tag'>> {
    const url = `${this.baseUrl}/api/posts/videos?limit=${limit}&offset=${offset}`
    const response = await this.fetch(url, false, accessToken)

    console.log('[TapApiClient] getVideoFeed response:', {
      hasPosts: !!response.posts,
      postsCount: response.posts?.length,
      total: response.total,
      limit: response.limit,
      offset: response.offset,
    })

    // Log first post to see exact backend structure
    if (response.posts && response.posts.length > 0) {
      console.log('[TapApiClient] Sample backend post structure:', {
        uri: response.posts[0].uri?.slice(-20),
        hasAuthor: !!response.posts[0].author,
        authorStructure: response.posts[0].author ? Object.keys(response.posts[0].author) : [],
        hasEmbed: !!response.posts[0].embed,
        embedType: response.posts[0].embed?.$type,
        embedStructure: response.posts[0].embed ? Object.keys(response.posts[0].embed) : [],
        hasRecord: !!response.posts[0].record,
        recordHasEmbed: !!response.posts[0].record?.embed,
        createdAt: response.posts[0].createdAt,
        hasTags: !!response.posts[0].tags,
      })
    }

    // Transform posts to AT Protocol format
    if (response.posts) {
      response.posts = response.posts.map((post: any) => {
        // Backend returns: {uri, did, cid, author: {did, handle}, createdAt, text, record, embed, ...}
        // We need AT Protocol PostView format

        // Use the author object from backend if available
        const author = post.author || {
          did: post.did,
          handle: 'user.bsky.social',
          displayName: 'User',
          avatar: '',
          viewer: {},
          labels: [],
        }

        // Handle embed - backend already provides it in the response
        let embed = post.embed

        // If no embed at root level, check if it's nested in record
        if (!embed && post.record?.embed) {
          embed = post.record.embed
        }

        const transformedPost = {
          uri: post.uri,
          cid: post.cid,
          author,
          record: post.record,
          embed,
          replyCount: post.replyCount || post.reply_count || 0,
          repostCount: post.repostCount || post.repost_count || 0,
          likeCount: post.likeCount || post.like_count || 0,
          quoteCount: post.quoteCount || post.quote_count || 0,
          // Backend uses createdAt (camelCase) with Unix timestamp
          indexedAt: post.createdAt
            ? new Date(post.createdAt * 1000).toISOString()
            : post.indexedAt || new Date().toISOString(),
          viewer: post.viewer || {},
          labels: post.labels || [],
          // TAP extensions
          hasImages: post.hasImages ?? post.has_images ?? false,
          hasVideo: post.hasVideo ?? post.has_video ?? true, // Should always be true for video feed
          hasGif: post.hasGif ?? post.has_gif ?? false,
          imageCount: post.imageCount ?? post.image_count ?? 0,
          tags: post.tags || [], // Default to empty array if not provided
          tagsComplete: post.tagsComplete ?? post.tags_complete ?? false,
        }

        console.log('[TapApiClient] Transformed post:', {
          uri: transformedPost.uri?.slice(-20),
          hasAuthor: !!transformedPost.author,
          hasEmbed: !!transformedPost.embed,
          embedType: transformedPost.embed?.$type,
          embedStructure: transformedPost.embed ? Object.keys(transformedPost.embed) : [],
          hasRecord: !!transformedPost.record,
          indexedAt: transformedPost.indexedAt,
        })

        const normalized = this.normalizePost(transformedPost)

        console.log('[TapApiClient] After normalization:', {
          uri: normalized.uri?.slice(-20),
          embedType: normalized.embed?.$type,
          embedStructure: normalized.embed ? Object.keys(normalized.embed) : [],
          hasPlaylist: normalized.embed?.playlist,
        })

        return normalized
      })
    }

    return response
  }

  /**
   * Get all tags - calls TAP API directly (now has CORS enabled)
   */
  async getAllTags(): Promise<TapTag[]> {
    const url = `${this.baseUrl}/api/tags`

    console.log('[TapApiClient] getAllTags - Making request:', {
      url,
      baseUrl: this.baseUrl,
      hasAccessToken: false,
    })

    const response = await this.fetch(url, false)

    console.log('[TapApiClient] getAllTags - Response received:', {
      isArray: Array.isArray(response),
      hasTags: !!response?.tags,
      responseType: typeof response,
      keys: response ? Object.keys(response) : [],
      sampleData: Array.isArray(response)
        ? response[0]
        : response?.tags?.[0] || response,
    })

    // Handle both direct array and { tags: [] } response formats
    const tagsArray = Array.isArray(response) ? response : response.tags || response

    console.log('[TapApiClient] getAllTags - Parsed tags array:', {
      isArray: Array.isArray(tagsArray),
      count: Array.isArray(tagsArray) ? tagsArray.length : 'not an array',
      firstTag: Array.isArray(tagsArray) ? tagsArray[0] : null,
    })

    // Transform snake_case fields to camelCase
    const transformed = tagsArray.map((tag: any) => ({
      id: tag.id,
      name: tag.name,
      slug: tag.slug,
      description: tag.description,
      color: tag.color,
      createdAt: tag.created_at || tag.createdAt, // Support both formats
      postCount: tag.post_count || tag.postCount, // Add post count
    }))

    console.log('[TapApiClient] getAllTags - Transformed tags:', {
      count: transformed.length,
      firstTag: transformed[0],
    })

    return transformed
  }

  /**
   * Get explore data - user-created feeds and starter packs
   * Calls /api/explore (public endpoint)
   */
  async getExplore(): Promise<ExploreResponse> {
    const url = `${this.baseUrl}/api/explore`
    return this.fetch(url, false)
  }

  /**
   * Internal fetch method with error handling
   */
  private async fetch(endpoint: string, requiresAuth: boolean, accessToken?: string): Promise<any> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Forward user's Bluesky access token to TAP API
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
      console.log('[DEBUG TapApiClient] Request with auth:', {
        endpoint: endpoint.replace(this.baseUrl, ''),
        hasAuth: true,
        tokenPreview: accessToken.slice(0, 20) + '...',
      })
    } else {
      console.log('[DEBUG TapApiClient] Request WITHOUT auth:', {
        endpoint: endpoint.replace(this.baseUrl, ''),
        hasAuth: false,
      })
    }

    try {
      console.log('[TapApiClient] Making request:', {
        endpoint,
        baseUrl: this.baseUrl,
        headers: Object.keys(headers),
      })

      const response = await fetch(endpoint, {
        method: 'GET',
        headers,
      })

      console.log('[TapApiClient] Response received:', {
        endpoint: endpoint.replace(this.baseUrl, ''),
        status: response.status,
        ok: response.ok,
        statusText: response.statusText,
      })

      if (!response.ok) {
        const errorText = await response.text()
        const errorMessage = `TAP API error (${response.status}): ${errorText || response.statusText}`
        console.error('[TapApiClient] API Error:', {
          endpoint: endpoint.replace(this.baseUrl, ''),
          status: response.status,
          errorText,
        })
        throw new Error(errorMessage)
      }

      const data = await response.json()
      console.log('[TapApiClient] Response parsed:', {
        endpoint: endpoint.replace(this.baseUrl, ''),
        hasData: !!data,
        dataType: Array.isArray(data) ? 'array' : typeof data,
      })

      return data
    } catch (error) {
      console.error('[TapApiClient] Request failed:', {
        endpoint: endpoint.replace(this.baseUrl, ''),
        error: error instanceof Error ? error.message : String(error),
        errorType: error?.constructor?.name,
      })

      if (error instanceof Error) {
        throw error
      }
      throw new Error(`TAP API request failed: ${String(error)}`)
    }
  }
}
